import { useSupabaseLoader } from "../../../server/supabase/hooks";
import {
  ACCOUNT_LIMIT_KEYS,
  type AccountLimitKey,
  SupabaseAccountLimitService,
} from "../server/supabase-account-service";

type AccountLimitTypeMap = {
  [K in keyof typeof ACCOUNT_LIMIT_KEYS]: (typeof ACCOUNT_LIMIT_KEYS)[K] extends { type: "number" }
    ? number
    : (typeof ACCOUNT_LIMIT_KEYS)[K] extends { type: "boolean" }
      ? boolean
      : (typeof ACCOUNT_LIMIT_KEYS)[K] extends { type: "select"; values: readonly (infer V)[] }
        ? V
        : never;
};

export function useAccountLimit<K extends AccountLimitKey>(
  key: K,
  defaultValue?: AccountLimitTypeMap[K],
): ReturnType<typeof useSupabaseLoader<AccountLimitTypeMap[K]>> {
  return useSupabaseLoader(
    async ({ supabase }) => {
      const { data, error } = await new SupabaseAccountLimitService(supabase).getKey(key);

      if (error) throw new Error(error.message);

      const raw = data?.count;

      if (raw === null || raw === undefined) {
        if (defaultValue !== undefined) {
          return defaultValue;
        }
        throw new Error(`No account limit found for key: ${key}`);
      }

      if (ACCOUNT_LIMIT_KEYS[key].type === "boolean") {
        return Boolean(raw) as AccountLimitTypeMap[K];
      }
      if (ACCOUNT_LIMIT_KEYS[key].type === "number") {
        return raw as AccountLimitTypeMap[K];
      }

      // Select keys need explicit mapping to their options.
      if (ACCOUNT_LIMIT_KEYS[key].type === "select") {
        return ACCOUNT_LIMIT_KEYS[key].values[raw] as AccountLimitTypeMap[K];
      }
      throw new Error(`Unsupported account limit key type for key: ${key}`);
    },
    [key, defaultValue],
  );
}
